import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Eye, EyeOff, Image as ImageIcon } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import sliderService from '../../services/sliderService';
import { getFullImageUrl } from '../../utils/imageUpload';
import SliderModal from '../../components/Modals/SliderModal';
import toast from 'react-hot-toast';

const SlidersContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const SlidersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const SliderCard = styled(Card)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 212, 255, 0.15);
    border-color: #00d4ff;
  }
`;

const SliderImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SliderContent = styled.div`
  padding: 20px;
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const SliderTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.span`
  background: ${props => props.active ? 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)' : 'linear-gradient(135deg, #6c6c8a 0%, #5a5a7a 100%)'};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const SliderDescription = styled.p`
  color: #b8b8d1;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 15px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SliderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.span`
  color: #6c6c8a;
  font-size: 12px;
`;

const DetailValue = styled.span`
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
`;

const SliderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: ${props => {
    switch(props.variant) {
      case 'edit': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'delete': return 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)';
      case 'activate': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'deactivate': return 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #5a5a7a 100%)';
    }
  }};
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const Sliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSliderModal, setShowSliderModal] = useState(false);
  const [selectedSlider, setSelectedSlider] = useState(null);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      setLoading(true);
      const data = await sliderService.getSliders();
      setSliders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sliders:', error);
      toast.error('خطا در بارگذاری اسلایدرها');
    } finally {
      setLoading(false);
    }
  };

  const filteredSliders = sliders.filter(slider =>
    slider.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    slider.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddSlider = () => {
    setSelectedSlider(null);
    setShowSliderModal(true);
  };

  const handleEditSlider = (slider) => {
    setSelectedSlider(slider);
    setShowSliderModal(true);
  };

  const handleDeleteSlider = async (slider) => {
    if (window.confirm('آیا از حذف این اسلایدر اطمینان دارید؟')) {
      try {
        await sliderService.deleteSlider(slider.id);
        toast.success('اسلایدر با موفقیت حذف شد');
        fetchSliders();
      } catch (error) {
        toast.error('خطا در حذف اسلایدر');
        console.error('Error deleting slider:', error);
      }
    }
  };

  const handleToggleStatus = async (slider) => {
    try {
      if (slider.isActive) {
        await sliderService.deactivateSlider(slider.id);
        toast.success('اسلایدر غیرفعال شد');
      } else {
        await sliderService.activateSlider(slider.id);
        toast.success('اسلایدر فعال شد');
      }
      fetchSliders();
    } catch (error) {
      toast.error('خطا در تغییر وضعیت اسلایدر');
      console.error('Error toggling slider status:', error);
    }
  };

  const handleSliderModalClose = () => {
    setShowSliderModal(false);
    setSelectedSlider(null);
  };

  const handleSliderModalSuccess = () => {
    fetchSliders();
  };

  if (loading) {
    return (
      <SlidersContainer>
        <LoadingSpinner>
          <div>در حال بارگذاری...</div>
        </LoadingSpinner>
      </SlidersContainer>
    );
  }

  return (
    <SlidersContainer>
      <Header>
        <Title>مدیریت اسلایدرها</Title>
        <SearchContainer>
          <Input
            type="text"
            placeholder="جستجو در اسلایدرها..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search size={16} />}
          />
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <Button variant="primary" onClick={handleAddSlider} icon={<Plus size={16} />}>
            افزودن اسلایدر
          </Button>
        </SearchContainer>
      </Header>

      <SliderModal
        show={showSliderModal}
        slider={selectedSlider}
        onClose={handleSliderModalClose}
        onSuccess={handleSliderModalSuccess}
      />

      {filteredSliders.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🖼️</EmptyIcon>
          <EmptyTitle>هیچ اسلایدری یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'هیچ اسلایدری با این جستجو یافت نشد' : 'هنوز هیچ اسلایدری ایجاد نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <SlidersGrid>
          {filteredSliders.map((slider) => (
            <motion.div
              key={slider.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SliderCard>
                <SliderImage>
                  {slider.picture ? (
                    <img src={getFullImageUrl(slider.picture)} alt={slider.title} />
                  ) : (
                    <ImageIcon size={48} color="#ffffff" />
                  )}
                </SliderImage>
                
                <SliderContent>
                  <SliderHeader>
                    <SliderTitle>{slider.title}</SliderTitle>
                    <StatusBadge active={slider.isActive}>
                      {slider.isActive ? 'فعال' : 'غیرفعال'}
                    </StatusBadge>
                  </SliderHeader>
                  
                  <SliderDescription>{slider.description}</SliderDescription>
                  
                  <SliderDetails>
                    <DetailItem>
                      <DetailLabel>شناسه:</DetailLabel>
                      <DetailValue>#{slider.id}</DetailValue>
                    </DetailItem>
                    {slider.btnText && (
                      <DetailItem>
                        <DetailLabel>دکمه:</DetailLabel>
                        <DetailValue>{slider.btnText}</DetailValue>
                      </DetailItem>
                    )}
                    {slider.btnUrl && (
                      <DetailItem>
                        <DetailLabel>لینک:</DetailLabel>
                        <DetailValue>{slider.btnUrl}</DetailValue>
                      </DetailItem>
                    )}
                  </SliderDetails>
                  
                  <SliderActions>
                    <ActionButton variant="edit" onClick={() => handleEditSlider(slider)}>
                      <Edit size={14} />
                      ویرایش
                    </ActionButton>
                    <ActionButton 
                      variant={slider.isActive ? 'deactivate' : 'activate'} 
                      onClick={() => handleToggleStatus(slider)}
                    >
                      {slider.isActive ? <EyeOff size={14} /> : <Eye size={14} />}
                      {slider.isActive ? 'غیرفعال' : 'فعال'}
                    </ActionButton>
                    <ActionButton variant="delete" onClick={() => handleDeleteSlider(slider)}>
                      <Trash2 size={14} />
                      حذف
                    </ActionButton>
                  </SliderActions>
                </SliderContent>
              </SliderCard>
            </motion.div>
          ))}
        </SlidersGrid>
      )}
    </SlidersContainer>
  );
};

export default Sliders;
